/**
 * lib/db.ts
 * Dexie database setup for the trading journal application
 * 
 * Defines all persistent data tables with proper indexing for performance.
 * Uses snake_case table names following database conventions while
 * maintaining TypeScript interface compatibility.
 */

import Dexie, { Table } from 'dexie';

// Import all persistent schema types
import type { Account } from '@/types/schema/Account';
import type { AccountRisk } from '@/types/schema/AccountRisk';
import type { BalanceHistory } from '@/types/schema/BalanceHistory';
import type { BasicSettings } from '@/types/schema/BasicSettings';
import type { Broker } from '@/types/schema/Broker';
import type { EconomicCalendarSettings } from '@/types/schema/EconomicCalendarSettings';
import type { EconomicRelease } from '@/types/schema/EconomicRelease';
import type { EntryModel } from '@/types/schema/EntryModel';
import type { EntryModelCategory } from '@/types/schema/EntryModelCategory';
import type { Instrument } from '@/types/schema/Instrument';
import type { LiveTradingSettings } from '@/types/schema/LiveTradingSettings';
import type { MarketState } from '@/types/schema/MarketState';
import type { Confluence } from '@/types/schema/Confluence';
import type { OpportunitySettings } from '@/types/schema/OpportunitySettings';
import type { Playbook } from '@/types/schema/Playbook';
import type { MarketConditionCategory } from '@/types/schema/MarketConditionCategory';
import type { PlaybookCriteriaItem } from '@/types/schema/PlaybookCriteriaItem';
import type { PostTradingSettings } from '@/types/schema/PostTradingSettings';
import type { PreTradingSettings } from '@/types/schema/PreTradingSettings';
import type { Session } from '@/types/schema/Session';
import type { Strategy } from '@/types/schema/Strategy';
import type { Trade } from '@/types/schema/Trade';
import type { TradeExecution } from '@/types/schema/TradeExecution';
import type { TradeExecutionEvent } from '@/types/schema/TradeExecutionEvent';
import type { TradeOpportunity } from '@/types/schema/TradeOpportunity';
import type { TradingObjective } from '@/types/schema/TradingObjective';
import type { TradingObjectiveSettings } from '@/types/schema/TradingObjectiveSettings';
import type { MarketSnapshotSettings } from '@/types/schema/MarketSnapshotSettings';
import type { TradingSetupSettings } from '@/types/schema/TradingSetupSettings';
import type { FeeVerificationRecord } from '@/types/schema/FeeVerificationRecord';
import type { StoredImage, StoredImageBlob } from '@/types/schema/StoredImage';
import type { SyncState } from '@/types/sync/SyncState';
import type { SyncSettings } from '@/types/sync/SyncSettings';
import type { DirectoryHandle } from '@/types/sync/DirectoryHandle';
import type { OAuthToken } from '@/types/sync/OAuthToken';
import type { SessionDraft } from '@/types/transient/SessionDraft';
import type { OnboardingDraft } from '@/types/transient/OnboardingDraft';
import type { NoteDraft } from '@/types/transient/NoteDraft';
import type { Note } from '@/types/schema/Note';
import type { StoredSyncOperation } from '@/types/sync/SyncOperation';
import type { AICredential, AIEncryptionKeyRecord } from '@/types/shared/AI';
import type { RetiredDraftLineage } from '@/types/schema/RetiredDraftLineage';

/**
 * Main Database Class
 *
 * Defines all 24 persistent data tables with indexes optimized for
 * common query patterns identified in the functional requirements.
 */
export class AppDatabase extends Dexie {
  // ===== CORE ENTITIES =====
  
  /** Trading accounts with broker linkage */
  accounts!: Table<Account>;
  
  /** Risk profiles and cascade rules per account */
  account_risks!: Table<AccountRisk>;
  
  /** Transaction log with running balance tracking */
  balance_histories!: Table<BalanceHistory>;
  
  /** Trading sessions (daily containers) */
  sessions!: Table<Session>;
  
  /** Trade setups and strategy analysis */
  trades!: Table<Trade>;
  
  /** Account-specific trade executions */
  trade_executions!: Table<TradeExecution>;

  /** Immutable append-only execution event log */
  trade_execution_events!: Table<TradeExecutionEvent>;
  
  /** Missed or cancelled trading opportunities */
  trade_opportunities!: Table<TradeOpportunity>;
  
  /** Philosophical strategy containers */
  strategies!: Table<Strategy>;
  
  /** Trading strategy definitions */
  playbooks!: Table<Playbook>;
  
  /** Session goals with usage tracking */
  trading_objectives!: Table<TradingObjective>;
  
  /** Economic calendar events and market impact */
  economic_releases!: Table<EconomicRelease>;
  
  /** Market condition snapshots per session */
  market_states!: Table<MarketState>;
  
  // ===== SETTINGS ENTITIES =====
  
  /** Core app configuration */
  basic_settings!: Table<BasicSettings>;
  
  /** Pre-trading ritual configuration */
  pre_trading_settings!: Table<PreTradingSettings>;
  
  /** Live trading interface configuration */
  live_trading_settings!: Table<LiveTradingSettings>;
  
  /** Post-session review configuration */
  post_trading_settings!: Table<PostTradingSettings>;
  
  /** Opportunity tracking configuration */
  opportunity_settings!: Table<OpportunitySettings>;

  /** Trading objective settings (objective types, etc.) */
  trading_objective_settings!: Table<TradingObjectiveSettings>;

  /** Market snapshot settings (snapshot field definitions) */
  market_snapshot_settings!: Table<MarketSnapshotSettings>;

  /** Economic calendar settings (API integration, filtering) */
  economic_calendar_settings!: Table<EconomicCalendarSettings>;

  /** Trading setup settings (default strategy-playbook pairs, account, instruments) */
  trading_setup_settings!: Table<TradingSetupSettings>;

  // ===== REFERENCE/LOOKUP ENTITIES =====
  
  /** Trading instrument specifications */
  instruments!: Table<Instrument>;
  
  /** Broker-specific fee structures */
  brokers!: Table<Broker>;
  
  /** Fee verification records tracking user-verified fees */
  fee_verification_records!: Table<FeeVerificationRecord>;
  
  /** Market condition categories for organizing criteria and confluences */
  market_condition_categories!: Table<MarketConditionCategory>;
  
  /** Reusable playbook entry criteria */
  playbook_criteria_items!: Table<PlaybookCriteriaItem>;
  
  /** Reusable market factors for playbook evaluation */
  confluences!: Table<Confluence>;

  /** Entry model categories for organizing execution strategies */
  entry_model_categories!: Table<EntryModelCategory>;

  /** Specific entry methodologies */
  entry_models!: Table<EntryModel>;

  // ===== ASSET/MEDIA ENTITIES =====

  /** Stored image metadata (synced) */
  stored_images!: Table<StoredImage>;

  /** Stored image blobs (local-only, per-device) */
  stored_image_blobs!: Table<StoredImageBlob>;

  // ===== AI CREDENTIAL ENTITIES (device-local, NOT synced) =====

  /** Encrypted AI provider API keys */
  ai_credentials!: Table<AICredential>;

  /** Device-local AES-GCM encryption key for AI credentials */
  ai_encryption_keys!: Table<AIEncryptionKeyRecord>;

  // ===== SYNC ENTITIES =====

  /** Sync state tracking for incremental sync */
  sync_state!: Table<SyncState>;

  /** Sync settings - user preferences for sync behavior */
  sync_settings!: Table<SyncSettings>;

  /** Directory handles for File System Access API persistence */
  directory_handles!: Table<DirectoryHandle>;

  /** OAuth tokens for cloud provider authentication */
  oauth_tokens!: Table<OAuthToken>;

  /** Sync operations log for operation-based sync */
  sync_operations!: Table<StoredSyncOperation>;

  // ===== TRANSIENT/DRAFT ENTITIES =====

  /** Session drafts - work-in-progress trading sessions */
  session_drafts!: Table<SessionDraft>;

  /** Wizard drafts - onboarding and other wizard states */
  wizard_drafts!: Table<OnboardingDraft>;

  /** Note drafts - work-in-progress notes during trading sessions */
  note_drafts!: Table<NoteDraft>;

  /** Notes - persisted notes with ownership and soft references */
  notes!: Table<Note>;

  /** Permanently retired carry-forward lineages (keyed by rootDraftId) */
  retired_draft_lineages!: Table<RetiredDraftLineage>;

  constructor() {
    super('AppDatabase');
    
    this.version(1).stores({
      // ===== CORE ENTITIES =====
      
      // Accounts with broker and organization indexes
      accounts: '&id, brokerId, status, createdAt',
      
      // Account risks with account reference
      account_risks: '&id, accountId, status, createdAt',
      
      // Balance history with critical compound index for chronological queries
      balance_histories: '&id, [accountId+timestamp], accountId, tradeExecutionId, timestamp, createdAt',
      
      // Sessions with date-based queries (most common lookup pattern)
      sessions: '&id, date, sessionType, createdAt',
      
      // Trades with session and time-based indexing
      trades: '&id, sessionId, createdAt',
      
      // Trade executions with critical relationships
      trade_executions: '&id, tradeId, accountId, createdAt, entryTime, exitTime',
      
      // Trade opportunities with session indexing
      trade_opportunities: '&id, sessionId, createdAt',
      
      // Strategies with status filtering
      strategies: '&id, status, createdAt',
      
      // Playbooks with status filtering for active playbooks
      playbooks: '&id, status, createdAt',
      
      // Trading objectives with status filtering for active objectives
      trading_objectives: '&id, status, createdAt',
      
      // Economic releases with datetime indexing for calendar views
      economic_releases: '&id, datetime, currency, impactLevel, createdAt',
      
      // Market states with session+instrument compound indexing
      market_states: '&id, [sessionId+instrumentId], sessionId, instrumentId, timeframe, createdAt',
      
      // ===== SETTINGS ENTITIES =====
      // Settings typically have single records, minimal indexing needed
      
      basic_settings: '&id, createdAt',
      pre_trading_settings: '&id, createdAt',
      live_trading_settings: '&id, createdAt', 
      post_trading_settings: '&id, createdAt',
      opportunity_settings: '&id, createdAt',
      
      // ===== REFERENCE/LOOKUP ENTITIES =====
      
      // Instruments with symbol lookups and status filtering
      instruments: '&id, symbol, type, status, createdAt, updatedAt',

      // Brokers with status filtering
      brokers: '&id, status, createdAt, updatedAt',
      
      // Fee verification records with broker and instrument lookups
      fee_verification_records: '&id, brokerId, [brokerId+instrumentId], [brokerId+category], createdAt',
      
      // Market condition categories with ordering
      market_condition_categories: '&id, status, order, createdAt',

      // Criteria items with category grouping and status filtering
      playbook_criteria_items: '&id, marketConditionCategoryId, status, createdAt',

      // Negative confluences with category grouping and status filtering
      negative_confluences: '&id, marketConditionCategoryId, status, createdAt',

      // Entry model categories with ordering
      entry_model_categories: '&id, status, order, createdAt',

      // Entry models with category grouping and status filtering
      entry_models: '&id, entryModelCategoryId, status, createdAt',

      // ===== SYNC ENTITIES =====

      // Sync state tracking (single record per device, keyed by device ID)
      sync_state: '&id, lastSyncedAt, syncProvider, createdAt'
    });

    // ========================================
    // PRE-BETA DEVELOPMENT VERSIONS (1-7)
    // These versions are for development/testing only
    // When ready for beta release, schema will be consolidated to version 1.0
    // ========================================

    // Version 2 (PRE-BETA): Add transient/draft tables
    this.version(2).stores({
      // ===== TRANSIENT/DRAFT ENTITIES =====

      // Session drafts with date and status indexing
      session_drafts: '&id, date, status, createdAt, updatedAt',

      // Wizard drafts with screen tracking
      wizard_drafts: '&id, currentScreen, createdAt, updatedAt'
    });

    // Version 3 (PRE-BETA): Add updatedAt indexes to all tables for sync change detection
    this.version(3).stores({
      // Add updatedAt to all synced tables (only specify changed indexes)
      accounts: '&id, brokerId, status, createdAt, updatedAt',
      account_risks: '&id, accountId, status, createdAt, updatedAt',
      balance_histories: '&id, [accountId+timestamp], accountId, tradeExecutionId, timestamp, createdAt, updatedAt',
      sessions: '&id, date, sessionType, createdAt, updatedAt',
      trades: '&id, sessionId, createdAt, updatedAt',
      trade_executions: '&id, tradeId, accountId, createdAt, entryTime, exitTime, updatedAt',
      trade_opportunities: '&id, sessionId, createdAt, updatedAt',
      strategies: '&id, status, createdAt, updatedAt',
      playbooks: '&id, status, createdAt, updatedAt',
      trading_objectives: '&id, status, createdAt, updatedAt',
      economic_releases: '&id, datetime, currency, impactLevel, createdAt, updatedAt',
      market_states: '&id, [sessionId+instrumentId], sessionId, instrumentId, timeframe, createdAt, updatedAt',
      basic_settings: '&id, createdAt, updatedAt',
      pre_trading_settings: '&id, createdAt, updatedAt',
      live_trading_settings: '&id, createdAt, updatedAt',
      post_trading_settings: '&id, createdAt, updatedAt',
      opportunity_settings: '&id, createdAt, updatedAt',
      fee_verification_records: '&id, brokerId, [brokerId+instrumentId], [brokerId+category], createdAt, updatedAt',
      market_condition_categories: '&id, status, order, createdAt, updatedAt',
      playbook_criteria_items: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      negative_confluences: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      entry_model_categories: '&id, status, order, createdAt, updatedAt',
      entry_models: '&id, entryModelCategoryId, status, createdAt, updatedAt'
    });

    // Version 4 (PRE-BETA): Add directory_handles table for File System Access API persistence
    this.version(4).stores({
      // Directory handles for local sync provider
      directory_handles: '&id, provider, createdAt'
    });

    // Version 5 (PRE-BETA): Add sync_settings table for user sync preferences
    this.version(5).stores({
      // Sync settings - user preferences for background sync behavior
      sync_settings: '&id, createdAt, updatedAt'
    });

    // Version 6 (PRE-BETA): Add updatedAt indexes to instruments and brokers for sync change detection
    this.version(6).stores({
      // Update instruments to include updatedAt index for sync
      instruments: '&id, symbol, type, status, createdAt, updatedAt',

      // Update brokers to include updatedAt index for sync
      brokers: '&id, status, createdAt, updatedAt'
    });

    // Version 7 (PRE-BETA): Add directoryId to sync_settings for directory identity tracking
    this.version(7).stores({
      // Update sync_settings to include directoryId for tracking which directory we're synced to
      sync_settings: '&id, createdAt, updatedAt, directoryId'
    });

    // Version 8 (PRE-BETA): Add oauth_tokens table for cloud provider authentication
    this.version(8).stores({
      // OAuth tokens for cloud sync providers (Google Drive, Dropbox, etc.)
      oauth_tokens: '&id, provider, expiresAt, createdAt'
    });

    // Version 9 (PRE-BETA): Re-declare all tables with updatedAt indexes
    // Note: sync_conflicts was added here but removed in version 11 (no longer needed with LWW sync)
    this.version(9).stores({
      // ===== ALL EXISTING TABLES (from version 8) =====
      accounts: '&id, brokerId, status, createdAt, updatedAt',
      account_risks: '&id, accountId, status, createdAt, updatedAt',
      balance_histories: '&id, [accountId+timestamp], accountId, tradeExecutionId, timestamp, createdAt, updatedAt',
      sessions: '&id, date, sessionType, createdAt, updatedAt',
      trades: '&id, sessionId, createdAt, updatedAt',
      trade_executions: '&id, tradeId, accountId, createdAt, entryTime, exitTime, updatedAt',
      trade_opportunities: '&id, sessionId, createdAt, updatedAt',
      strategies: '&id, status, createdAt, updatedAt',
      playbooks: '&id, status, createdAt, updatedAt',
      trading_objectives: '&id, status, createdAt, updatedAt',
      economic_releases: '&id, datetime, currency, impactLevel, createdAt, updatedAt',
      market_states: '&id, [sessionId+instrumentId], sessionId, instrumentId, timeframe, createdAt, updatedAt',
      basic_settings: '&id, createdAt, updatedAt',
      pre_trading_settings: '&id, createdAt, updatedAt',
      live_trading_settings: '&id, createdAt, updatedAt',
      post_trading_settings: '&id, createdAt, updatedAt',
      opportunity_settings: '&id, createdAt, updatedAt',
      instruments: '&id, symbol, type, status, createdAt, updatedAt',
      brokers: '&id, status, createdAt, updatedAt',
      fee_verification_records: '&id, brokerId, [brokerId+instrumentId], [brokerId+category], createdAt, updatedAt',
      market_condition_categories: '&id, status, order, createdAt, updatedAt',
      playbook_criteria_items: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      negative_confluences: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      entry_model_categories: '&id, status, order, createdAt, updatedAt',
      entry_models: '&id, entryModelCategoryId, status, createdAt, updatedAt',
      sync_state: '&id, lastSyncedAt, syncProvider, createdAt',
      session_drafts: '&id, date, status, createdAt, updatedAt',
      wizard_drafts: '&id, currentScreen, createdAt, updatedAt',
      directory_handles: '&id, provider, createdAt',
      sync_settings: '&id, createdAt, updatedAt, directoryId',
      oauth_tokens: '&id, provider, expiresAt, createdAt'
    });

    // Version 10 (PRE-BETA): Add sync_operations table for operation-based sync
    this.version(10).stores({
      // ===== ALL EXISTING TABLES (from version 9) =====
      accounts: '&id, brokerId, status, createdAt, updatedAt',
      account_risks: '&id, accountId, status, createdAt, updatedAt',
      balance_histories: '&id, [accountId+timestamp], accountId, tradeExecutionId, timestamp, createdAt, updatedAt',
      sessions: '&id, date, sessionType, createdAt, updatedAt',
      trades: '&id, sessionId, createdAt, updatedAt',
      trade_executions: '&id, tradeId, accountId, createdAt, entryTime, exitTime, updatedAt',
      trade_opportunities: '&id, sessionId, createdAt, updatedAt',
      strategies: '&id, status, createdAt, updatedAt',
      playbooks: '&id, status, createdAt, updatedAt',
      trading_objectives: '&id, status, createdAt, updatedAt',
      economic_releases: '&id, datetime, currency, impactLevel, createdAt, updatedAt',
      market_states: '&id, [sessionId+instrumentId], sessionId, instrumentId, timeframe, createdAt, updatedAt',
      basic_settings: '&id, createdAt, updatedAt',
      pre_trading_settings: '&id, createdAt, updatedAt',
      live_trading_settings: '&id, createdAt, updatedAt',
      post_trading_settings: '&id, createdAt, updatedAt',
      opportunity_settings: '&id, createdAt, updatedAt',
      instruments: '&id, symbol, type, status, createdAt, updatedAt',
      brokers: '&id, status, createdAt, updatedAt',
      fee_verification_records: '&id, brokerId, [brokerId+instrumentId], [brokerId+category], createdAt, updatedAt',
      market_condition_categories: '&id, status, order, createdAt, updatedAt',
      playbook_criteria_items: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      negative_confluences: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      entry_model_categories: '&id, status, order, createdAt, updatedAt',
      entry_models: '&id, entryModelCategoryId, status, createdAt, updatedAt',
      sync_state: '&id, lastSyncedAt, syncProvider, createdAt',
      session_drafts: '&id, date, status, createdAt, updatedAt',
      wizard_drafts: '&id, currentScreen, createdAt, updatedAt',
      directory_handles: '&id, provider, createdAt',
      sync_settings: '&id, createdAt, updatedAt, directoryId',
      oauth_tokens: '&id, provider, expiresAt, createdAt',

      // ===== NEW TABLE FOR VERSION 10 =====
      // Sync operations - local operation log for operation-based sync (NOT synced itself)
      // Primary key: opId (UUID)
      // Indexes:
      //   [deviceId+seq] - all ops for device X ordered by seq (compaction, debugging, recovery)
      //   batchId - batch grouping queries
      //   synced - simple count/filter queries
      //   [synced+deviceId+seq] - unsynced ops for device X ordered by seq (common export path)
      sync_operations: '&opId, [deviceId+seq], batchId, synced, [synced+deviceId+seq]'
    });

    // Version 11 (PRE-BETA): Drop sync_conflicts table
    // sync_conflicts is no longer needed - operation-based sync uses LWW (Last-Write-Wins)
    // resolution instead of user-facing conflict resolution.
    // Dexie drops tables by omitting them from the stores() declaration.
    this.version(11).stores({
      // ===== ALL TABLES (sync_conflicts omitted to drop it) =====
      accounts: '&id, brokerId, status, createdAt, updatedAt',
      account_risks: '&id, accountId, status, createdAt, updatedAt',
      balance_histories: '&id, [accountId+timestamp], accountId, tradeExecutionId, timestamp, createdAt, updatedAt',
      sessions: '&id, date, sessionType, createdAt, updatedAt',
      trades: '&id, sessionId, createdAt, updatedAt',
      trade_executions: '&id, tradeId, accountId, createdAt, entryTime, exitTime, updatedAt',
      trade_opportunities: '&id, sessionId, createdAt, updatedAt',
      strategies: '&id, status, createdAt, updatedAt',
      playbooks: '&id, status, createdAt, updatedAt',
      trading_objectives: '&id, status, createdAt, updatedAt',
      economic_releases: '&id, datetime, currency, impactLevel, createdAt, updatedAt',
      market_states: '&id, [sessionId+instrumentId], sessionId, instrumentId, timeframe, createdAt, updatedAt',
      basic_settings: '&id, createdAt, updatedAt',
      pre_trading_settings: '&id, createdAt, updatedAt',
      live_trading_settings: '&id, createdAt, updatedAt',
      post_trading_settings: '&id, createdAt, updatedAt',
      opportunity_settings: '&id, createdAt, updatedAt',
      instruments: '&id, symbol, type, status, createdAt, updatedAt',
      brokers: '&id, status, createdAt, updatedAt',
      fee_verification_records: '&id, brokerId, [brokerId+instrumentId], [brokerId+category], createdAt, updatedAt',
      market_condition_categories: '&id, status, order, createdAt, updatedAt',
      playbook_criteria_items: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      negative_confluences: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      entry_model_categories: '&id, status, order, createdAt, updatedAt',
      entry_models: '&id, entryModelCategoryId, status, createdAt, updatedAt',
      sync_state: '&id, lastSyncedAt, syncProvider, createdAt',
      session_drafts: '&id, date, status, createdAt, updatedAt',
      wizard_drafts: '&id, currentScreen, createdAt, updatedAt',
      directory_handles: '&id, provider, createdAt',
      sync_settings: '&id, createdAt, updatedAt, directoryId',
      oauth_tokens: '&id, provider, expiresAt, createdAt',
      sync_operations: '&opId, [deviceId+seq], batchId, synced, [synced+deviceId+seq]'
    });

    // Version 12 (PRE-BETA): Add trading_objective_settings table for objective type customization
    this.version(12).stores({
      // ===== ALL TABLES (from version 11 + new trading_objective_settings) =====
      accounts: '&id, brokerId, status, createdAt, updatedAt',
      account_risks: '&id, accountId, status, createdAt, updatedAt',
      balance_histories: '&id, [accountId+timestamp], accountId, tradeExecutionId, timestamp, createdAt, updatedAt',
      sessions: '&id, date, sessionType, createdAt, updatedAt',
      trades: '&id, sessionId, createdAt, updatedAt',
      trade_executions: '&id, tradeId, accountId, createdAt, entryTime, exitTime, updatedAt',
      trade_opportunities: '&id, sessionId, createdAt, updatedAt',
      strategies: '&id, status, createdAt, updatedAt',
      playbooks: '&id, status, createdAt, updatedAt',
      trading_objectives: '&id, status, createdAt, updatedAt',
      economic_releases: '&id, datetime, currency, impactLevel, createdAt, updatedAt',
      market_states: '&id, [sessionId+instrumentId], sessionId, instrumentId, timeframe, createdAt, updatedAt',
      basic_settings: '&id, createdAt, updatedAt',
      pre_trading_settings: '&id, createdAt, updatedAt',
      live_trading_settings: '&id, createdAt, updatedAt',
      post_trading_settings: '&id, createdAt, updatedAt',
      opportunity_settings: '&id, createdAt, updatedAt',
      instruments: '&id, symbol, type, status, createdAt, updatedAt',
      brokers: '&id, status, createdAt, updatedAt',
      fee_verification_records: '&id, brokerId, [brokerId+instrumentId], [brokerId+category], createdAt, updatedAt',
      market_condition_categories: '&id, status, order, createdAt, updatedAt',
      playbook_criteria_items: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      negative_confluences: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      entry_model_categories: '&id, status, order, createdAt, updatedAt',
      entry_models: '&id, entryModelCategoryId, status, createdAt, updatedAt',
      sync_state: '&id, lastSyncedAt, syncProvider, createdAt',
      session_drafts: '&id, date, status, createdAt, updatedAt',
      wizard_drafts: '&id, currentScreen, createdAt, updatedAt',
      directory_handles: '&id, provider, createdAt',
      sync_settings: '&id, createdAt, updatedAt, directoryId',
      oauth_tokens: '&id, provider, expiresAt, createdAt',
      sync_operations: '&opId, [deviceId+seq], batchId, synced, [synced+deviceId+seq]',

      // ===== NEW TABLE FOR VERSION 12 =====
      // Trading objective settings - user preferences for objective configuration
      trading_objective_settings: '&id, createdAt, updatedAt'
    });

    // Version 13 (PRE-BETA): Add stored_images and stored_image_blobs tables for image storage
    this.version(13).stores({
      // ===== ALL TABLES (from version 12 + new image storage tables) =====
      accounts: '&id, brokerId, status, createdAt, updatedAt',
      account_risks: '&id, accountId, status, createdAt, updatedAt',
      balance_histories: '&id, [accountId+timestamp], accountId, tradeExecutionId, timestamp, createdAt, updatedAt',
      sessions: '&id, date, sessionType, createdAt, updatedAt',
      trades: '&id, sessionId, createdAt, updatedAt',
      trade_executions: '&id, tradeId, accountId, createdAt, entryTime, exitTime, updatedAt',
      trade_opportunities: '&id, sessionId, createdAt, updatedAt',
      strategies: '&id, status, createdAt, updatedAt',
      playbooks: '&id, status, createdAt, updatedAt',
      trading_objectives: '&id, status, createdAt, updatedAt',
      economic_releases: '&id, datetime, currency, impactLevel, createdAt, updatedAt',
      market_states: '&id, [sessionId+instrumentId], sessionId, instrumentId, timeframe, createdAt, updatedAt',
      basic_settings: '&id, createdAt, updatedAt',
      pre_trading_settings: '&id, createdAt, updatedAt',
      live_trading_settings: '&id, createdAt, updatedAt',
      post_trading_settings: '&id, createdAt, updatedAt',
      opportunity_settings: '&id, createdAt, updatedAt',
      instruments: '&id, symbol, type, status, createdAt, updatedAt',
      brokers: '&id, status, createdAt, updatedAt',
      fee_verification_records: '&id, brokerId, [brokerId+instrumentId], [brokerId+category], createdAt, updatedAt',
      market_condition_categories: '&id, status, order, createdAt, updatedAt',
      playbook_criteria_items: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      negative_confluences: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      entry_model_categories: '&id, status, order, createdAt, updatedAt',
      entry_models: '&id, entryModelCategoryId, status, createdAt, updatedAt',
      sync_state: '&id, lastSyncedAt, syncProvider, createdAt',
      session_drafts: '&id, date, status, createdAt, updatedAt',
      wizard_drafts: '&id, currentScreen, createdAt, updatedAt',
      directory_handles: '&id, provider, createdAt',
      sync_settings: '&id, createdAt, updatedAt, directoryId',
      oauth_tokens: '&id, provider, expiresAt, createdAt',
      sync_operations: '&opId, [deviceId+seq], batchId, synced, [synced+deviceId+seq]',
      trading_objective_settings: '&id, createdAt, updatedAt',

      // ===== NEW TABLES FOR VERSION 13 =====
      // Stored images - synced metadata for chart screenshots and other images
      // Indexes: status for filtering, deletedAt for tombstone queries
      stored_images: '&id, status, deletedAt, createdAt, updatedAt',

      // Stored image blobs - local-only binary storage (NOT synced)
      // Primary key: imageId (unique, FK to stored_images)
      // Indexes: localSyncStatus for sync queries, lastAccessedAt for LRU cleanup
      stored_image_blobs: '&imageId, localSyncStatus, lastAccessedAt'
    });

    // Version 14 (PRE-BETA): Add market_snapshot_settings table for snapshot field configuration
    this.version(14).stores({
      // ===== ALL TABLES (from version 13 + new market_snapshot_settings) =====
      accounts: '&id, brokerId, status, createdAt, updatedAt',
      account_risks: '&id, accountId, status, createdAt, updatedAt',
      balance_histories: '&id, [accountId+timestamp], accountId, tradeExecutionId, timestamp, createdAt, updatedAt',
      sessions: '&id, date, sessionType, createdAt, updatedAt',
      trades: '&id, sessionId, createdAt, updatedAt',
      trade_executions: '&id, tradeId, accountId, createdAt, entryTime, exitTime, updatedAt',
      trade_opportunities: '&id, sessionId, createdAt, updatedAt',
      strategies: '&id, status, createdAt, updatedAt',
      playbooks: '&id, status, createdAt, updatedAt',
      trading_objectives: '&id, status, createdAt, updatedAt',
      economic_releases: '&id, datetime, currency, impactLevel, createdAt, updatedAt',
      market_states: '&id, [sessionId+instrumentId], sessionId, instrumentId, timeframe, createdAt, updatedAt',
      basic_settings: '&id, createdAt, updatedAt',
      pre_trading_settings: '&id, createdAt, updatedAt',
      live_trading_settings: '&id, createdAt, updatedAt',
      post_trading_settings: '&id, createdAt, updatedAt',
      opportunity_settings: '&id, createdAt, updatedAt',
      instruments: '&id, symbol, type, status, createdAt, updatedAt',
      brokers: '&id, status, createdAt, updatedAt',
      fee_verification_records: '&id, brokerId, [brokerId+instrumentId], [brokerId+category], createdAt, updatedAt',
      market_condition_categories: '&id, status, order, createdAt, updatedAt',
      playbook_criteria_items: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      negative_confluences: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      entry_model_categories: '&id, status, order, createdAt, updatedAt',
      entry_models: '&id, entryModelCategoryId, status, createdAt, updatedAt',
      sync_state: '&id, lastSyncedAt, syncProvider, createdAt',
      session_drafts: '&id, date, status, createdAt, updatedAt',
      wizard_drafts: '&id, currentScreen, createdAt, updatedAt',
      directory_handles: '&id, provider, createdAt',
      sync_settings: '&id, createdAt, updatedAt, directoryId',
      oauth_tokens: '&id, provider, expiresAt, createdAt',
      sync_operations: '&opId, [deviceId+seq], batchId, synced, [synced+deviceId+seq]',
      trading_objective_settings: '&id, createdAt, updatedAt',
      stored_images: '&id, status, deletedAt, createdAt, updatedAt',
      stored_image_blobs: '&imageId, localSyncStatus, lastAccessedAt',

      // ===== NEW TABLE FOR VERSION 14 =====
      // Market snapshot settings - user preferences for market snapshot field configuration
      market_snapshot_settings: '&id, createdAt, updatedAt'
    });

    // Version 15: Remove instrumentId from market_states (now uses instrumentSnapshot)
    this.version(15).stores({
      // ===== ALL TABLES (from version 14, market_states index updated) =====
      accounts: '&id, brokerId, status, createdAt, updatedAt',
      account_risks: '&id, accountId, status, createdAt, updatedAt',
      balance_histories: '&id, [accountId+timestamp], accountId, tradeExecutionId, timestamp, createdAt, updatedAt',
      sessions: '&id, date, sessionType, createdAt, updatedAt',
      trades: '&id, sessionId, createdAt, updatedAt',
      trade_executions: '&id, tradeId, accountId, createdAt, entryTime, exitTime, updatedAt',
      trade_opportunities: '&id, sessionId, createdAt, updatedAt',
      strategies: '&id, status, createdAt, updatedAt',
      playbooks: '&id, status, createdAt, updatedAt',
      trading_objectives: '&id, status, createdAt, updatedAt',
      economic_releases: '&id, datetime, currency, impactLevel, createdAt, updatedAt',
      // CHANGED: Removed [sessionId+instrumentId] compound index and instrumentId index
      // instrumentId now lives inside instrumentSnapshot (matches TradeExecution pattern)
      market_states: '&id, sessionId, timeframe, createdAt, updatedAt',
      basic_settings: '&id, createdAt, updatedAt',
      pre_trading_settings: '&id, createdAt, updatedAt',
      live_trading_settings: '&id, createdAt, updatedAt',
      post_trading_settings: '&id, createdAt, updatedAt',
      opportunity_settings: '&id, createdAt, updatedAt',
      instruments: '&id, symbol, type, status, createdAt, updatedAt',
      brokers: '&id, status, createdAt, updatedAt',
      fee_verification_records: '&id, brokerId, [brokerId+instrumentId], [brokerId+category], createdAt, updatedAt',
      market_condition_categories: '&id, status, order, createdAt, updatedAt',
      playbook_criteria_items: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      negative_confluences: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      entry_model_categories: '&id, status, order, createdAt, updatedAt',
      entry_models: '&id, entryModelCategoryId, status, createdAt, updatedAt',
      sync_state: '&id, lastSyncedAt, syncProvider, createdAt',
      session_drafts: '&id, date, status, createdAt, updatedAt',
      wizard_drafts: '&id, currentScreen, createdAt, updatedAt',
      directory_handles: '&id, provider, createdAt',
      sync_settings: '&id, createdAt, updatedAt, directoryId',
      oauth_tokens: '&id, provider, expiresAt, createdAt',
      sync_operations: '&opId, [deviceId+seq], batchId, synced, [synced+deviceId+seq]',
      trading_objective_settings: '&id, createdAt, updatedAt',
      stored_images: '&id, status, deletedAt, createdAt, updatedAt',
      stored_image_blobs: '&imageId, localSyncStatus, lastAccessedAt',
      market_snapshot_settings: '&id, createdAt, updatedAt'
    });

    // Version 16: Add economic_calendar_settings table, rename currency→country index
    this.version(16).stores({
      // ===== ALL TABLES (from version 15 + economic_calendar_settings) =====
      accounts: '&id, brokerId, status, createdAt, updatedAt',
      account_risks: '&id, accountId, status, createdAt, updatedAt',
      balance_histories: '&id, [accountId+timestamp], accountId, tradeExecutionId, timestamp, createdAt, updatedAt',
      sessions: '&id, date, sessionType, createdAt, updatedAt',
      trades: '&id, sessionId, createdAt, updatedAt',
      trade_executions: '&id, tradeId, accountId, createdAt, entryTime, exitTime, updatedAt',
      trade_opportunities: '&id, sessionId, createdAt, updatedAt',
      strategies: '&id, status, createdAt, updatedAt',
      playbooks: '&id, status, createdAt, updatedAt',
      trading_objectives: '&id, status, createdAt, updatedAt',
      // CHANGED: Renamed currency index to country (field renamed in EconomicRelease type)
      economic_releases: '&id, datetime, country, impactLevel, createdAt, updatedAt',
      market_states: '&id, sessionId, timeframe, createdAt, updatedAt',
      basic_settings: '&id, createdAt, updatedAt',
      pre_trading_settings: '&id, createdAt, updatedAt',
      live_trading_settings: '&id, createdAt, updatedAt',
      post_trading_settings: '&id, createdAt, updatedAt',
      opportunity_settings: '&id, createdAt, updatedAt',
      instruments: '&id, symbol, type, status, createdAt, updatedAt',
      brokers: '&id, status, createdAt, updatedAt',
      fee_verification_records: '&id, brokerId, [brokerId+instrumentId], [brokerId+category], createdAt, updatedAt',
      market_condition_categories: '&id, status, order, createdAt, updatedAt',
      playbook_criteria_items: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      negative_confluences: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      entry_model_categories: '&id, status, order, createdAt, updatedAt',
      entry_models: '&id, entryModelCategoryId, status, createdAt, updatedAt',
      sync_state: '&id, lastSyncedAt, syncProvider, createdAt',
      session_drafts: '&id, date, status, createdAt, updatedAt',
      wizard_drafts: '&id, currentScreen, createdAt, updatedAt',
      directory_handles: '&id, provider, createdAt',
      sync_settings: '&id, createdAt, updatedAt, directoryId',
      oauth_tokens: '&id, provider, expiresAt, createdAt',
      sync_operations: '&opId, [deviceId+seq], batchId, synced, [synced+deviceId+seq]',
      trading_objective_settings: '&id, createdAt, updatedAt',
      stored_images: '&id, status, deletedAt, createdAt, updatedAt',
      stored_image_blobs: '&imageId, localSyncStatus, lastAccessedAt',
      market_snapshot_settings: '&id, createdAt, updatedAt',

      // ===== NEW TABLE FOR VERSION 16 =====
      // Economic calendar settings - user preferences for economic calendar configuration
      economic_calendar_settings: '&id, createdAt, updatedAt'
    });

    // Version 17 (PRE-BETA): Add note_drafts and notes tables for note entity foundation
    this.version(17).stores({
      // ===== ALL TABLES (from version 16 + new note tables) =====
      accounts: '&id, brokerId, status, createdAt, updatedAt',
      account_risks: '&id, accountId, status, createdAt, updatedAt',
      balance_histories: '&id, [accountId+timestamp], accountId, tradeExecutionId, timestamp, createdAt, updatedAt',
      sessions: '&id, date, sessionType, createdAt, updatedAt',
      trades: '&id, sessionId, createdAt, updatedAt',
      trade_executions: '&id, tradeId, accountId, createdAt, entryTime, exitTime, updatedAt',
      trade_opportunities: '&id, sessionId, createdAt, updatedAt',
      strategies: '&id, status, createdAt, updatedAt',
      playbooks: '&id, status, createdAt, updatedAt',
      trading_objectives: '&id, status, createdAt, updatedAt',
      economic_releases: '&id, datetime, country, impactLevel, createdAt, updatedAt',
      market_states: '&id, sessionId, timeframe, createdAt, updatedAt',
      basic_settings: '&id, createdAt, updatedAt',
      pre_trading_settings: '&id, createdAt, updatedAt',
      live_trading_settings: '&id, createdAt, updatedAt',
      post_trading_settings: '&id, createdAt, updatedAt',
      opportunity_settings: '&id, createdAt, updatedAt',
      instruments: '&id, symbol, type, status, createdAt, updatedAt',
      brokers: '&id, status, createdAt, updatedAt',
      fee_verification_records: '&id, brokerId, [brokerId+instrumentId], [brokerId+category], createdAt, updatedAt',
      market_condition_categories: '&id, status, order, createdAt, updatedAt',
      playbook_criteria_items: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      negative_confluences: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      entry_model_categories: '&id, status, order, createdAt, updatedAt',
      entry_models: '&id, entryModelCategoryId, status, createdAt, updatedAt',
      sync_state: '&id, lastSyncedAt, syncProvider, createdAt',
      session_drafts: '&id, date, status, createdAt, updatedAt',
      wizard_drafts: '&id, currentScreen, createdAt, updatedAt',
      directory_handles: '&id, provider, createdAt',
      sync_settings: '&id, createdAt, updatedAt, directoryId',
      oauth_tokens: '&id, provider, expiresAt, createdAt',
      sync_operations: '&opId, [deviceId+seq], batchId, synced, [synced+deviceId+seq]',
      trading_objective_settings: '&id, createdAt, updatedAt',
      stored_images: '&id, status, deletedAt, createdAt, updatedAt',
      stored_image_blobs: '&imageId, localSyncStatus, lastAccessedAt',
      market_snapshot_settings: '&id, createdAt, updatedAt',
      economic_calendar_settings: '&id, createdAt, updatedAt',

      // ===== NEW TABLES FOR VERSION 17 =====
      // Note drafts - work-in-progress notes during trading sessions
      // Compound index [noteType+ownerId] for cascade delete lookups
      note_drafts: '&id, [noteType+ownerId], createdAt, updatedAt',

      // Notes - persisted notes with ownership model
      // Compound index [noteType+ownerId] for cascade delete lookups
      // For 'analysis' type notes, ownerId is undefined
      notes: '&id, [noteType+ownerId], createdAt, updatedAt'
    });

    // Version 18 (PRE-BETA): Rename negative_confluences to confluences (neutral polarity model)
    // Confluences are now inherently neutral; polarity is assigned at playbook level
    // IMPORTANT: negative_confluences explicitly set to null to delete the table created by v1-v17
    this.version(18).stores({
      // ===== ALL TABLES (from version 17, negative_confluences deleted and replaced with confluences) =====
      accounts: '&id, brokerId, status, createdAt, updatedAt',
      account_risks: '&id, accountId, status, createdAt, updatedAt',
      balance_histories: '&id, [accountId+timestamp], accountId, tradeExecutionId, timestamp, createdAt, updatedAt',
      sessions: '&id, date, sessionType, createdAt, updatedAt',
      trades: '&id, sessionId, createdAt, updatedAt',
      trade_executions: '&id, tradeId, accountId, createdAt, entryTime, exitTime, updatedAt',
      trade_opportunities: '&id, sessionId, createdAt, updatedAt',
      strategies: '&id, status, createdAt, updatedAt',
      playbooks: '&id, status, createdAt, updatedAt',
      trading_objectives: '&id, status, createdAt, updatedAt',
      economic_releases: '&id, datetime, country, impactLevel, createdAt, updatedAt',
      market_states: '&id, sessionId, timeframe, createdAt, updatedAt',
      basic_settings: '&id, createdAt, updatedAt',
      pre_trading_settings: '&id, createdAt, updatedAt',
      live_trading_settings: '&id, createdAt, updatedAt',
      post_trading_settings: '&id, createdAt, updatedAt',
      opportunity_settings: '&id, createdAt, updatedAt',
      instruments: '&id, symbol, type, status, createdAt, updatedAt',
      brokers: '&id, status, createdAt, updatedAt',
      fee_verification_records: '&id, brokerId, [brokerId+instrumentId], [brokerId+category], createdAt, updatedAt',
      market_condition_categories: '&id, status, order, createdAt, updatedAt',
      playbook_criteria_items: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      // RENAMED: negative_confluences → confluences (neutral polarity model)
      confluences: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      // DELETED: Explicitly drop table created by v1-v17 (omitting doesn't delete, must use null)
      negative_confluences: null,
      entry_model_categories: '&id, status, order, createdAt, updatedAt',
      entry_models: '&id, entryModelCategoryId, status, createdAt, updatedAt',
      sync_state: '&id, lastSyncedAt, syncProvider, createdAt',
      session_drafts: '&id, date, status, createdAt, updatedAt',
      wizard_drafts: '&id, currentScreen, createdAt, updatedAt',
      directory_handles: '&id, provider, createdAt',
      sync_settings: '&id, createdAt, updatedAt, directoryId',
      oauth_tokens: '&id, provider, expiresAt, createdAt',
      sync_operations: '&opId, [deviceId+seq], batchId, synced, [synced+deviceId+seq]',
      trading_objective_settings: '&id, createdAt, updatedAt',
      stored_images: '&id, status, deletedAt, createdAt, updatedAt',
      stored_image_blobs: '&imageId, localSyncStatus, lastAccessedAt',
      market_snapshot_settings: '&id, createdAt, updatedAt',
      economic_calendar_settings: '&id, createdAt, updatedAt',
      note_drafts: '&id, [noteType+ownerId], createdAt, updatedAt',
      notes: '&id, [noteType+ownerId], createdAt, updatedAt'
    });

    // Version 19 (PRE-BETA): Add ai_credentials and ai_encryption_keys tables for AI credential storage
    // Both tables are device-local only (NOT synced) — API keys never leave the browser
    this.version(19).stores({
      // ===== ALL TABLES (from version 18 + new AI credential tables) =====
      accounts: '&id, brokerId, status, createdAt, updatedAt',
      account_risks: '&id, accountId, status, createdAt, updatedAt',
      balance_histories: '&id, [accountId+timestamp], accountId, tradeExecutionId, timestamp, createdAt, updatedAt',
      sessions: '&id, date, sessionType, createdAt, updatedAt',
      trades: '&id, sessionId, createdAt, updatedAt',
      trade_executions: '&id, tradeId, accountId, createdAt, entryTime, exitTime, updatedAt',
      trade_opportunities: '&id, sessionId, createdAt, updatedAt',
      strategies: '&id, status, createdAt, updatedAt',
      playbooks: '&id, status, createdAt, updatedAt',
      trading_objectives: '&id, status, createdAt, updatedAt',
      economic_releases: '&id, datetime, country, impactLevel, createdAt, updatedAt',
      market_states: '&id, sessionId, timeframe, createdAt, updatedAt',
      basic_settings: '&id, createdAt, updatedAt',
      pre_trading_settings: '&id, createdAt, updatedAt',
      live_trading_settings: '&id, createdAt, updatedAt',
      post_trading_settings: '&id, createdAt, updatedAt',
      opportunity_settings: '&id, createdAt, updatedAt',
      instruments: '&id, symbol, type, status, createdAt, updatedAt',
      brokers: '&id, status, createdAt, updatedAt',
      fee_verification_records: '&id, brokerId, [brokerId+instrumentId], [brokerId+category], createdAt, updatedAt',
      market_condition_categories: '&id, status, order, createdAt, updatedAt',
      playbook_criteria_items: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      confluences: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      entry_model_categories: '&id, status, order, createdAt, updatedAt',
      entry_models: '&id, entryModelCategoryId, status, createdAt, updatedAt',
      sync_state: '&id, lastSyncedAt, syncProvider, createdAt',
      session_drafts: '&id, date, status, createdAt, updatedAt',
      wizard_drafts: '&id, currentScreen, createdAt, updatedAt',
      directory_handles: '&id, provider, createdAt',
      sync_settings: '&id, createdAt, updatedAt, directoryId',
      oauth_tokens: '&id, provider, expiresAt, createdAt',
      sync_operations: '&opId, [deviceId+seq], batchId, synced, [synced+deviceId+seq]',
      trading_objective_settings: '&id, createdAt, updatedAt',
      stored_images: '&id, status, deletedAt, createdAt, updatedAt',
      stored_image_blobs: '&imageId, localSyncStatus, lastAccessedAt',
      market_snapshot_settings: '&id, createdAt, updatedAt',
      economic_calendar_settings: '&id, createdAt, updatedAt',
      note_drafts: '&id, [noteType+ownerId], createdAt, updatedAt',
      notes: '&id, [noteType+ownerId], createdAt, updatedAt',

      // ===== NEW TABLES FOR VERSION 19 =====
      // AI credentials - encrypted API keys for AI providers (device-local, NOT synced)
      // Unique provider index ensures one credential per provider (upsert pattern)
      ai_credentials: '&id, &provider, createdAt, updatedAt',

      // AI encryption keys - device-local AES-GCM key material (NOT synced)
      // Single record per device (id = 'device-key')
      ai_encryption_keys: '&id, createdAt'
    });

    // Version 20 (PRE-BETA): WU2B Execution Foundation
    // Changes:
    //   - trade_execution_events: NEW immutable append-only event log
    //   - trade_executions: removed entryTime/exitTime; added executionStatus/openedAt
    //   - trades: added unique &rootDraftId (promotion idempotency + drawer mode lookup)
    //   - balance_histories: added sourceExecutionEventId (canonical event linkage)
    // Upgrade function:
    //   1. Tombstones flat-model TradeExecution records (pre-WU2B, cannot migrate)
    //   2. Strips phase2Execution/qualitativeReview from session_drafts.trades[] (PR #60 artifact)
    this.version(20).stores({
      // ===== ALL TABLES (from version 19, with 4 index changes) =====
      accounts: '&id, brokerId, status, createdAt, updatedAt',
      account_risks: '&id, accountId, status, createdAt, updatedAt',
      // CHANGED: added sourceExecutionEventId index (canonical event linkage)
      balance_histories: '&id, [accountId+timestamp], accountId, tradeExecutionId, sourceExecutionEventId, timestamp, createdAt, updatedAt',
      sessions: '&id, date, sessionType, createdAt, updatedAt',
      // CHANGED: added unique &rootDraftId (promotion idempotency + drawer mode lookup)
      trades: '&id, &rootDraftId, sessionId, createdAt, updatedAt',
      // CHANGED: removed entryTime/exitTime (flat model); added executionStatus/openedAt (event-projection model)
      trade_executions: '&id, tradeId, accountId, executionStatus, openedAt, createdAt, updatedAt',
      trade_opportunities: '&id, sessionId, createdAt, updatedAt',
      strategies: '&id, status, createdAt, updatedAt',
      playbooks: '&id, status, createdAt, updatedAt',
      trading_objectives: '&id, status, createdAt, updatedAt',
      economic_releases: '&id, datetime, country, impactLevel, createdAt, updatedAt',
      market_states: '&id, sessionId, timeframe, createdAt, updatedAt',
      basic_settings: '&id, createdAt, updatedAt',
      pre_trading_settings: '&id, createdAt, updatedAt',
      live_trading_settings: '&id, createdAt, updatedAt',
      post_trading_settings: '&id, createdAt, updatedAt',
      opportunity_settings: '&id, createdAt, updatedAt',
      instruments: '&id, symbol, type, status, createdAt, updatedAt',
      brokers: '&id, status, createdAt, updatedAt',
      fee_verification_records: '&id, brokerId, [brokerId+instrumentId], [brokerId+category], createdAt, updatedAt',
      market_condition_categories: '&id, status, order, createdAt, updatedAt',
      playbook_criteria_items: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      confluences: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      entry_model_categories: '&id, status, order, createdAt, updatedAt',
      entry_models: '&id, entryModelCategoryId, status, createdAt, updatedAt',
      sync_state: '&id, lastSyncedAt, syncProvider, createdAt',
      session_drafts: '&id, date, status, createdAt, updatedAt',
      wizard_drafts: '&id, currentScreen, createdAt, updatedAt',
      directory_handles: '&id, provider, createdAt',
      sync_settings: '&id, createdAt, updatedAt, directoryId',
      oauth_tokens: '&id, provider, expiresAt, createdAt',
      sync_operations: '&opId, [deviceId+seq], batchId, synced, [synced+deviceId+seq]',
      trading_objective_settings: '&id, createdAt, updatedAt',
      stored_images: '&id, status, deletedAt, createdAt, updatedAt',
      stored_image_blobs: '&imageId, localSyncStatus, lastAccessedAt',
      market_snapshot_settings: '&id, createdAt, updatedAt',
      economic_calendar_settings: '&id, createdAt, updatedAt',
      note_drafts: '&id, [noteType+ownerId], createdAt, updatedAt',
      notes: '&id, [noteType+ownerId], createdAt, updatedAt',
      ai_credentials: '&id, &provider, createdAt, updatedAt',
      ai_encryption_keys: '&id, createdAt',

      // ===== NEW TABLE FOR VERSION 20 =====
      // Immutable execution event log — append-only, projection source for TradeExecution
      // &operationId: unique index — enforces idempotency at DB level (service-layer check is first line of defence)
      trade_execution_events: '&id, tradeExecutionId, tradeId, sessionId, &operationId, eventType, occurredAt, createdAt, updatedAt',
    }).upgrade(async (tx) => {
      const now = new Date().toISOString();

      // 1. Tombstone flat-model TradeExecution records (pre-WU2B promotion boundary).
      //    Old records carry 'direction' or 'entryPrice' as top-level fields — these are
      //    incompatible with the event-projection model and cannot be migrated automatically.
      const executions = await tx.table('trade_executions').toArray() as Array<Record<string, unknown>>;
      const staleIds = executions
        .filter((e) => 'direction' in e || 'entryPrice' in e)
        .map((e) => e['id'] as string);
      if (staleIds.length > 0) {
        await Promise.all(
          staleIds.map((id) =>
            tx.table('trade_executions').update(id, { deletedAt: now, updatedAt: now })
          )
        );
      }

      // 2. Strip phase2Execution and qualitativeReview from session_drafts.trades[].
      //    These fields were added by PR #60 (rejected) and must be removed from all drafts.
      const sessionDrafts = await tx.table('session_drafts').toArray() as Array<Record<string, unknown>>;
      for (const draft of sessionDrafts) {
        const trades = draft['trades'] as Array<Record<string, unknown>> | undefined;
        if (!trades || trades.length === 0) continue;
        let modified = false;
        const cleaned = trades.map((trade) => {
          if (!('phase2Execution' in trade) && !('qualitativeReview' in trade)) return trade;
          modified = true;
          const copy = { ...trade };
          delete copy['phase2Execution'];
          delete copy['qualitativeReview'];
          return copy;
        });
        if (modified) {
          await tx.table('session_drafts').update(draft['id'] as string, {
            trades: cleaned,
            updatedAt: now,
          });
        }
      }
    });

    // Version 21 (PRE-BETA): WU2C Carry-Forward — RetiredDraftLineage table
    // Changes:
    //   - retired_draft_lineages: NEW table for "Delete Forever" lineage retirement records
    //     Keyed by rootDraftId (unique) — one record per lineage, synced across devices.
    //     No upgrade function needed (new table, starts empty).
    this.version(21).stores({
      accounts: '&id, brokerId, status, createdAt, updatedAt',
      account_risks: '&id, accountId, status, createdAt, updatedAt',
      balance_histories: '&id, [accountId+timestamp], accountId, tradeExecutionId, sourceExecutionEventId, timestamp, createdAt, updatedAt',
      sessions: '&id, date, sessionType, createdAt, updatedAt',
      trades: '&id, &rootDraftId, sessionId, createdAt, updatedAt',
      trade_executions: '&id, tradeId, accountId, executionStatus, openedAt, createdAt, updatedAt',
      trade_opportunities: '&id, sessionId, createdAt, updatedAt',
      strategies: '&id, status, createdAt, updatedAt',
      playbooks: '&id, status, createdAt, updatedAt',
      trading_objectives: '&id, status, createdAt, updatedAt',
      economic_releases: '&id, datetime, country, impactLevel, createdAt, updatedAt',
      market_states: '&id, sessionId, timeframe, createdAt, updatedAt',
      basic_settings: '&id, createdAt, updatedAt',
      pre_trading_settings: '&id, createdAt, updatedAt',
      live_trading_settings: '&id, createdAt, updatedAt',
      post_trading_settings: '&id, createdAt, updatedAt',
      opportunity_settings: '&id, createdAt, updatedAt',
      instruments: '&id, symbol, type, status, createdAt, updatedAt',
      brokers: '&id, status, createdAt, updatedAt',
      fee_verification_records: '&id, brokerId, [brokerId+instrumentId], [brokerId+category], createdAt, updatedAt',
      market_condition_categories: '&id, status, order, createdAt, updatedAt',
      playbook_criteria_items: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      confluences: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      entry_model_categories: '&id, status, order, createdAt, updatedAt',
      entry_models: '&id, entryModelCategoryId, status, createdAt, updatedAt',
      sync_state: '&id, lastSyncedAt, syncProvider, createdAt',
      session_drafts: '&id, date, status, createdAt, updatedAt',
      wizard_drafts: '&id, currentScreen, createdAt, updatedAt',
      directory_handles: '&id, provider, createdAt',
      sync_settings: '&id, createdAt, updatedAt, directoryId',
      oauth_tokens: '&id, provider, expiresAt, createdAt',
      sync_operations: '&opId, [deviceId+seq], batchId, synced, [synced+deviceId+seq]',
      trading_objective_settings: '&id, createdAt, updatedAt',
      stored_images: '&id, status, deletedAt, createdAt, updatedAt',
      stored_image_blobs: '&imageId, localSyncStatus, lastAccessedAt',
      market_snapshot_settings: '&id, createdAt, updatedAt',
      economic_calendar_settings: '&id, createdAt, updatedAt',
      note_drafts: '&id, [noteType+ownerId], createdAt, updatedAt',
      notes: '&id, [noteType+ownerId], createdAt, updatedAt',
      ai_credentials: '&id, &provider, createdAt, updatedAt',
      ai_encryption_keys: '&id, createdAt',
      trade_execution_events: '&id, tradeExecutionId, tradeId, sessionId, &operationId, eventType, occurredAt, createdAt, updatedAt',

      // ===== NEW TABLE FOR VERSION 21 =====
      retired_draft_lineages: '&id, &rootDraftId, retiredBySessionDraftId',
    });

    // Version 22 (PRE-BETA): WU7C T05 — TradingSetupSettings table
    // Changes:
    //   - trading_setup_settings: NEW table for Trading Setup default preferences
    //     Singleton semantics (one record per user), synced across devices.
    //     No upgrade function needed (new table, starts empty).
    this.version(22).stores({
      accounts: '&id, brokerId, status, createdAt, updatedAt',
      account_risks: '&id, accountId, status, createdAt, updatedAt',
      balance_histories: '&id, [accountId+timestamp], accountId, tradeExecutionId, sourceExecutionEventId, timestamp, createdAt, updatedAt',
      sessions: '&id, date, sessionType, createdAt, updatedAt',
      trades: '&id, &rootDraftId, sessionId, createdAt, updatedAt',
      trade_executions: '&id, tradeId, accountId, executionStatus, openedAt, createdAt, updatedAt',
      trade_opportunities: '&id, sessionId, createdAt, updatedAt',
      strategies: '&id, status, createdAt, updatedAt',
      playbooks: '&id, status, createdAt, updatedAt',
      trading_objectives: '&id, status, createdAt, updatedAt',
      economic_releases: '&id, datetime, country, impactLevel, createdAt, updatedAt',
      market_states: '&id, sessionId, timeframe, createdAt, updatedAt',
      basic_settings: '&id, createdAt, updatedAt',
      pre_trading_settings: '&id, createdAt, updatedAt',
      live_trading_settings: '&id, createdAt, updatedAt',
      post_trading_settings: '&id, createdAt, updatedAt',
      opportunity_settings: '&id, createdAt, updatedAt',
      instruments: '&id, symbol, type, status, createdAt, updatedAt',
      brokers: '&id, status, createdAt, updatedAt',
      fee_verification_records: '&id, brokerId, [brokerId+instrumentId], [brokerId+category], createdAt, updatedAt',
      market_condition_categories: '&id, status, order, createdAt, updatedAt',
      playbook_criteria_items: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      confluences: '&id, marketConditionCategoryId, status, createdAt, updatedAt',
      entry_model_categories: '&id, status, order, createdAt, updatedAt',
      entry_models: '&id, entryModelCategoryId, status, createdAt, updatedAt',
      sync_state: '&id, lastSyncedAt, syncProvider, createdAt',
      session_drafts: '&id, date, status, createdAt, updatedAt',
      wizard_drafts: '&id, currentScreen, createdAt, updatedAt',
      directory_handles: '&id, provider, createdAt',
      sync_settings: '&id, createdAt, updatedAt, directoryId',
      oauth_tokens: '&id, provider, expiresAt, createdAt',
      sync_operations: '&opId, [deviceId+seq], batchId, synced, [synced+deviceId+seq]',
      trading_objective_settings: '&id, createdAt, updatedAt',
      stored_images: '&id, status, deletedAt, createdAt, updatedAt',
      stored_image_blobs: '&imageId, localSyncStatus, lastAccessedAt',
      market_snapshot_settings: '&id, createdAt, updatedAt',
      economic_calendar_settings: '&id, createdAt, updatedAt',
      note_drafts: '&id, [noteType+ownerId], createdAt, updatedAt',
      notes: '&id, [noteType+ownerId], createdAt, updatedAt',
      ai_credentials: '&id, &provider, createdAt, updatedAt',
      ai_encryption_keys: '&id, createdAt',
      trade_execution_events: '&id, tradeExecutionId, tradeId, sessionId, &operationId, eventType, occurredAt, createdAt, updatedAt',
      retired_draft_lineages: '&id, &rootDraftId, retiredBySessionDraftId',

      // ===== NEW TABLE FOR VERSION 22 =====
      trading_setup_settings: '&id, createdAt, updatedAt',
    });
  }
}

// Export singleton instance
export const db = new AppDatabase();

// Setup change detection hooks for sync
// Import dynamically to avoid circular dependencies
import { SYNC_TABLES } from '@/constants/syncTables';
import { type SyncTransaction } from '@/services/sync/SyncImportContext';

/**
 * Warn if a write bypasses instrumented paths (BaseService, persistOnboardingData).
 * Dev-only to avoid production log spam. One warning per table per transaction.
 *
 * @param trans - Transaction object from Dexie hook
 * @param tableName - Name of the table being written to
 * @param operation - Type of operation (creating/updating/deleting)
 * @param primKey - Primary key of the record
 */
function warnIfUninstrumentedWrite(
  trans: unknown,
  tableName: string,
  operation: string,
  primKey: unknown
): void {
  // Only warn in development
  if (!import.meta.env.DEV) return;

  const syncTrans = trans as SyncTransaction | undefined;

  // Skip if in instrumented context (BaseService, persistOnboardingData) or import context
  if (syncTrans?.isInstrumented || syncTrans?.isImport) return;

  // Dedupe: one warning per table per transaction
  if (syncTrans?._warnedTables?.has(tableName)) return;

  // Log warning with actionable info
  console.warn(
    `[Sync Warning] Direct write on '${tableName}' (${operation}, key: ${primKey}). ` +
    `This bypasses op logging. If intentional, mark transaction with markAsInstrumentedWriteContext().`
  );

  // Mark as warned for this table (initialize Set if needed)
  if (syncTrans) {
    if (!syncTrans._warnedTables) {
      syncTrans._warnedTables = new Set();
    }
    syncTrans._warnedTables.add(tableName);
  }
}

/**
 * Setup Dexie hooks to detect data changes for sync
 *
 * Two purposes:
 * 1. Set flag in BackgroundSyncService for beforeunload warnings (all writes)
 * 2. Safety net: warn on direct writes that bypass instrumented paths (dev-only)
 */
SYNC_TABLES.forEach((tableName) => {
  const table = db[tableName];
  if (table && typeof table.hook === 'function') {
    // Hook into all CRUD operations
    // Use function() syntax to receive Dexie hook arguments (primKey, obj, trans)
    table.hook('creating', function(primKey, _obj, trans) {
      // Safety net warning for uninstrumented writes
      warnIfUninstrumentedWrite(trans, tableName, 'creating', primKey);

      // Dynamically import to avoid circular dependency
      import('@/services/sync/BackgroundSyncService').then(({ BackgroundSyncService }) => {
        BackgroundSyncService.getInstance().markDataChanged();
      }).catch(() => {
        // Silently fail if BackgroundSyncService not available (e.g., during initial setup)
      });
    });

    table.hook('updating', function(_modifications, primKey, _obj, trans) {
      // Safety net warning for uninstrumented writes
      warnIfUninstrumentedWrite(trans, tableName, 'updating', primKey);

      import('@/services/sync/BackgroundSyncService').then(({ BackgroundSyncService }) => {
        BackgroundSyncService.getInstance().markDataChanged();
      }).catch(() => {
        // Silently fail
      });
    });

    table.hook('deleting', function(primKey, _obj, trans) {
      // Safety net warning for uninstrumented writes
      warnIfUninstrumentedWrite(trans, tableName, 'deleting', primKey);

      import('@/services/sync/BackgroundSyncService').then(({ BackgroundSyncService }) => {
        BackgroundSyncService.getInstance().markDataChanged();
      }).catch(() => {
        // Silently fail
      });
    });
  }
});

/**
 * Initialize database with basic error handling
 * 
 * TODO: [CONTRACTOR] Add comprehensive error handling:
 * - Quota exceeded detection
 * - Browser compatibility checks  
 * - Migration failure recovery
 * - Safe mode fallback
 */
export const initializeDatabase = async (): Promise<boolean> => {
  try {
    // Test basic database functionality
    await db.open();
    
    console.log('✅ Database initialized successfully');
    console.log(`📊 Database version: ${db.verno}`);
    console.log(`🗄️ Tables: ${db.tables.length}`);
    
    return true;
  } catch (error) {
    console.error('❌ Database initialization failed:', error);
    
    // TODO: [CONTRACTOR] Implement fallback strategies:
    // - Retry with exponential backoff
    // - Fall back to localStorage safe mode
    // - Show user-friendly error with recovery options
    
    throw error;
  }
};

/**
 * Database health check utility
 * 
 * Verifies database is accessible and returns basic stats.
 * Useful for debugging and system status displays.
 */
export const getDatabaseHealth = async () => {
  try {
    const [
      accountCount,
      sessionCount, 
      tradeCount,
      tradeExecutionCount
    ] = await Promise.all([
      db.accounts.count(),
      db.sessions.count(),
      db.trades.count(),
      db.trade_executions.count()
    ]);
    
    return {
      status: 'healthy',
      version: db.verno,
      tableCount: db.tables.length,
      recordCounts: {
        accounts: accountCount,
        sessions: sessionCount,
        trades: tradeCount,
        tradeExecutions: tradeExecutionCount
      }
    };
  } catch (error) {
    return {
      status: 'error',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Emergency database reset utility
 * 
 * WARNING: This will delete all data! Only for development/testing.
 * In production, this should require explicit user confirmation.
 */
export const resetDatabase = async (): Promise<void> => {
  console.warn('🚨 Resetting database - all data will be lost!');
  
  try {
    await db.delete();
    await db.open();
    console.log('✅ Database reset complete');
  } catch (error) {
    console.error('❌ Database reset failed:', error);
    throw error;
  }
};

// Export database instance as default for convenience
export default db;